import { useEffect, useId, useRef, useState } from "react";
import InputError from "./InputError";
import getClassName from "../../utilities/getClassName";
import Label from "./Label";
import errorCross from "../../assets/icons/error-cross.svg";
import tickGreen from "../../assets/icons/tick-green.svg";
import Fieldset from "./Fieldset";
import CharacterCount from "./CharacterCount";

const TextAreaInput = ({
    value,
    label,
    id: suppliedId,
    required,
    validation,
    defaultValue,
    maxLength,
    showCharacterCount = false,
    disabled,
    validateForm,
}) => {
    const generatedId = useId();
    const id = suppliedId || generatedId;
    const [characterCount, setCharacterCount] = useState(String(defaultValue ?? "").length);

    // State
    const [error, setError] = useState("");
    const [focussed, setFocussed] = useState(false);
    const [interacted, setInteracted] = useState(false);
    const [skipAnimation, setSkipAnimation] = useState(false);

    // Hooks
    const input = useRef();

    // Validate input
    const validationHandler = () => {
        if (showCharacterCount) setCharacterCount(input.current.value.length);
        if (value) {
            value(false);

            const currentValue = input.current.value.replace(/\s/g, "");

            switch (true) {
                case required && currentValue.length === 0:
                    setError(`${label.replace("*", "")} is required`);
                    return;
                case required && validation === "email" && !/\S+@\S+\.\S+/.test(currentValue):
                    setError(`Invalid email`);
                    return;
                default:
                    setInteracted(true);
                    setError("");
                    value(input.current.value);
                    return;
            }
        }
    };

    // "Latest ref" pattern so the validation effect below doesn't have to
    // depend on `validationHandler` (which would be re-created every render —
    // and many callers pass an inline arrow for `value`, so wrapping in
    // useCallback wouldn't make it stable).
    const validationHandlerRef = useRef(validationHandler);
    validationHandlerRef.current = validationHandler;

    //On blur handler
    const onBlurHandler = () => {
        validationHandler();
        setSkipAnimation(false);
        const currentValue = input.current.value.replace(/\s/g, "");
        if (currentValue.length === 0) {
            setFocussed(false);
        }
    };

    // Force validation / Check for autofill
    useEffect(() => {
        if (validateForm) {
            validationHandlerRef.current();
        }
        if (input.current.value) {
            setFocussed(true);
            setSkipAnimation(true);
        }
        if (showCharacterCount) setCharacterCount(input.current.value.length);
    }, [defaultValue, validateForm, showCharacterCount]);

    return (
        <Fieldset>
            <div className="text-area-input">
                {error && <InputError>{error}</InputError>}
                <textarea
                    ref={input}
                    id={id}
                    name={id}
                    autoComplete="on"
                    onFocus={() => setFocussed(true)}
                    maxLength={maxLength}
                    aria-describedby={showCharacterCount ? `${id}-count` : undefined}
                    onBlur={onBlurHandler}
                    onChange={validationHandler}
                    className={getClassName([
                        "text-area-input__text-area",
                        error && "text-area-input__text-area--error",
                        interacted && !error && "text-area-input__text-area--success",
                    ])}
                    disabled={disabled}
                    defaultValue={defaultValue}
                />

                {error && <img className="text-area-input__error-cross" src={errorCross} alt="" />}
                {interacted && !error && <img className="text-area-input__tick" src={tickGreen} alt="" />}
                {label && (
                    <Label minimised={focussed} skipAnimation={skipAnimation} htmlFor={id}>
                        {label}
                    </Label>
                )}
            </div>
            {showCharacterCount && <CharacterCount id={`${id}-count`} count={characterCount} maxLength={maxLength} />}
        </Fieldset>
    );
};

export default TextAreaInput;
